import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { throwError, Observable, BehaviorSubject, tap, from } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';

import { AuthenticationService } from '@core/services/auth.service';
import { ModalServiceService } from '@core/services/modal-service.service';
import { EtagService } from '@core/services/etag.service';

import { environment } from '@environment/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isLoggingOut = false;
  private AUTH_HEADER = 'Authorization';
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authService: AuthenticationService,
    private cookieService: CookieService,
    private modalService: ModalServiceService,
    private router: Router,
    private etagService: EtagService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interception for non-API requests or during logout
    if (this.isLoggingOut || !req.url.startsWith(environment.backEndServiceURL)) {
      return this.handleEtag(next.handle(req));
    }

    // Set content type if not FormData
    if (!(req.body instanceof FormData) && !req.headers.has('Content-Type')) {
      req = req.clone({ headers: req.headers.set('Content-Type', 'application/json') });
    }

    // Add auth token
    req = this.addAuthenticationToken(req);

    return this.handleEtag(next.handle(req)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status >= 500) {
          this.modalService.openErrorModal(
            'Our servers are temporarily unavailable. We\'re working to resolve this issue and appreciate your patience. For immediate assistance, please visit <a href="https://support.confe.io" target="_blank" rel="noopener noreferrer">support.confe.io</a>.'
          );
          return throwError(() => error);
        }

        if (error.status === 401) {
          // If already logging out, don't attempt refresh
          if (this.isLoggingOut) {
            return throwError(() => new Error('Already logging out'));
          }

          // Handle token refresh
          if (this.refreshTokenInProgress) {
            return this.refreshTokenSubject.pipe(
              filter((result) => result !== null),
              take(1),
              switchMap(() => next.handle(this.addAuthenticationToken(req)))
            );
          }

          this.refreshTokenInProgress = true;
          this.refreshTokenSubject.next(null);

          return from(this.authService.persistCurrentUser()).pipe(
            switchMap(() => this.authService.refreshToken()),
            switchMap((newToken: any) => {
              this.refreshTokenInProgress = false;

              if (newToken?.access_token) {
                this.cookieService.delete('accessToken');
                this.cookieService.set('accessToken', newToken.access_token, { path: '/' });
                this.refreshTokenSubject.next(newToken.access_token);
                return this.handleEtag(next.handle(this.addAuthenticationToken(req)));
              }

              // No valid token - initiate logout
              return this.handleLogout();
            }),
            catchError((refreshError) => {
              if (refreshError.status === 401) {
                return this.handleLogout();
              }

              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(null);
              return this.handleLogout();
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.authService.getAccessToken();
    return token
      ? request.clone({ headers: request.headers.set(this.AUTH_HEADER, 'Bearer ' + token) })
      : request;
  }

  private handleLogout(): Observable<never> {
    if (this.isLoggingOut) {
      return throwError(() => new Error('Logout in progress'));
    }

    const currentUrl = this.router.url;
    const publicAuthRoutes = [
      '/auth/login',
      '/auth/signup',
      '/auth/forget-password',
      '/auth/reset-password',
      '/auth/verify',
      '/auth/verify-2fa',
    ];
    const isOnPublicAuthRoute = publicAuthRoutes.some((route) => currentUrl.startsWith(route));

    this.isLoggingOut = true;

    // On public auth pages, don't sign out or redirect (avoids bouncing from signup to login on 401)
    if (!isOnPublicAuthRoute) {
      this.authService.signOut();
      return throwError(() => {
        this.router.navigate(['/auth/login'], { replaceUrl: true });
        return new Error('Session expired. Please log in again.');
      });
    }

    this.isLoggingOut = false;
    return throwError(() => new Error('Session expired. Please log in again.'));
  }

  private handleEtag<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const newEtag = event.headers.get('etag');
          if (newEtag) {
            const oldEtag = localStorage.getItem('etag');
            if (oldEtag && oldEtag !== newEtag) {
              this.etagService.notifyEtagChanged();
            }
            localStorage.setItem('etag', newEtag);
          }
        }
      })
    );
  }
}
